import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Core from 'class/Core';
import {
    Card, Form, Row, Col, Select, DatePicker, Button,
    Table, Typography, Space, Spin, message, Statistic,
} from 'antd';
import * as Icons from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import ptBR from 'antd/es/date-picker/locale/pt_BR';
import { useMutation, useQuery } from 'react-query';
import config, { getBasicAuthHeaders } from 'utils/configAdapter';
import { mask } from 'utils/helpers';

dayjs.locale('pt-br');

// Detecta o ambiente para selecionar a URL do integrar
const getIntegrarUrl = () => {
    const hostname = window.location.hostname;
    const env = hostname === 'localhost' ? 'local'
        : hostname.includes('preprod') ? 'development'
        : 'production';
    return config[env].url;
};

const TrabalhadoresTotais = () => {
    const { Title, Text } = Typography;
    const [form] = Form.useForm();
    const core = new Core();

    const [tableData, setTableData] = useState([]);
    const [totaisData, setTotaisData] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const [gestorasDisponiveis, setGestorasDisponiveis] = useState([]);
    const [showGestoraField, setShowGestoraField] = useState(false);
    const [loadingExcel, setLoadingExcel] = useState(false);
    // Guarda os valores do último form submetido para uso no Excel
    const [lastParams, setLastParams] = useState(null);

    // Valida sessão ao montar
    useEffect(() => {
        core.isAuth();
    }, []);

    // Busca lista de favorecidos e assistência do usuário (Bearer Token da aplicação)
    const { data: queryFavorecidos = { list: [], beneficioId: null }, isLoading: loadingFavorecidos } = useQuery(
        'FAVORECIDOS-TRABALHADORES-TOTAIS',
        async () => {
            const response = await fetch(
                `${core.getUrl()}api-v1/empresa/relatorios/relacao-assistencia`,
                { method: 'GET', headers: core.getHeadersFetch(true) }
            );
            const result = await core.responseConvert(response);
            return {
                list: result.content.map((el) => ({
                    value: el.enti_cd_nseq,
                    label: el.enti_ds_sigla,
                })),
                // Quando assistencia vier preenchida, usa-a diretamente (sem exigir favorecido)
                beneficioId: result.assistencia ?? null,
            };
        },
        { onError: (err) => core.catchFecth(err) }
    );

    const favorecidos = queryFavorecidos.list;
    const beneficioId = queryFavorecidos.beneficioId;

    // Requisição principal ao integrar-api (Basic Auth)
    const { mutate: executeBusca, isLoading: loadingBusca } = useMutation(
        async (params) => {
            const queryParams = new URLSearchParams({
                mes: params.mes,
                gestora: params.gestora,
                ...(params.assistencia ? { assistencia: params.assistencia } : { favorecido: params.favorecido }),
            });

            const response = await fetch(
                `${getIntegrarUrl()}/assistencias/trabalhadores-totais?${queryParams}`,
                { method: 'GET', headers: getBasicAuthHeaders() }
            );

            if (!response.ok) {
                // Tenta extrair mensagem de erro do corpo da resposta
                let errorMsg;
                try {
                    const errorBody = await response.json();
                    errorMsg = errorBody.message || errorBody.detail || null;
                } catch (_) {
                    errorMsg = null;
                }

                if (response.status === 400) {
                    throw new Error(errorMsg || 'Parâmetros inválidos.');
                }
                if (response.status === 500) {
                    throw new Error('Erro interno do servidor. Tente novamente.');
                }
                throw new Error(errorMsg || `Erro ${response.status} ao buscar dados.`);
            }

            return response.json();
        },
        {
            onSuccess: (result) => {
                setTableData(result.content || []);
                setTotaisData(result.total || null);
                setShowTable(true);
                message.success(`${(result.content || []).length} registros encontrados.`);
            },
            onError: (err) => {
                message.error(err.message || 'Erro ao buscar dados.');
                setShowTable(false);
            },
        }
    );

    const { mutate: resolveGestoras, isLoading: loadingGestoras } = useMutation(
        async (params) => {
            const queryParams = new URLSearchParams(params);
            const response = await fetch(
                `${getIntegrarUrl()}/assistencias/gestoras-disponiveis?${queryParams}`,
                { method: 'GET', headers: getBasicAuthHeaders() }
            );

            if (!response.ok) {
                let errorMsg;
                try {
                    const errorBody = await response.json();
                    errorMsg = errorBody.message || errorBody.error || null;
                } catch (_) {
                    errorMsg = null;
                }

                throw new Error(errorMsg || 'Erro ao carregar gestoras.');
            }

            return response.json();
        },
        {
            onSuccess: (result) => {
                const gestoras = result.content?.gestoras || [];
                const gestoraDefault = result.content?.gestoraDefault || null;

                setGestorasDisponiveis(gestoras);
                setShowGestoraField(gestoras.length > 1);

                if (gestoras.length === 1 && gestoraDefault) {
                    form.setFieldsValue({ gestora: gestoraDefault });
                    updateFormValid({ ...form.getFieldsValue(), gestora: gestoraDefault });
                    form.validateFields(['gestora']).catch(() => null);
                    return;
                }

                form.setFieldsValue({ gestora: undefined });
                updateFormValid({ ...form.getFieldsValue(), gestora: undefined });

                if (!gestoras.length) {
                    message.warning('Nenhuma gestora disponível para o filtro selecionado.');
                }
            },
            onError: (err) => {
                setGestorasDisponiveis([]);
                setShowGestoraField(false);
                form.setFieldsValue({ gestora: undefined });
                updateFormValid({ ...form.getFieldsValue(), gestora: undefined });
                message.error(err.message || 'Erro ao carregar gestoras.');
            },
        }
    );

    const updateFormValid = (values) => {
        // Quando há beneficioId, gestora não é exigida (a API usa gestar por padrão)
        if (beneficioId) {
            setFormValid(!!(values.mes && values.favorecido));
        } else {
            setFormValid(!!(values.mes && values.favorecido && values.gestora));
        }
    };

    const handleFavorecidoChange = (favorecido) => {
        setGestorasDisponiveis([]);
        setShowGestoraField(false);
        setShowTable(false);
        setTotaisData(null);
        setTableData([]);
        setLastParams(null);
        form.setFieldsValue({ gestora: undefined });

        if (!favorecido) {
            updateFormValid(form.getFieldsValue());
            return;
        }

        // Quando há assistência do usuário, não precisa buscar gestoras (a API usa gestar por padrão)
        if (beneficioId) {
            updateFormValid({ ...form.getFieldsValue(), favorecido });
            return;
        }

        resolveGestoras({ favorecido: String(favorecido) });
    };

    const onFinish = (values) => {
        const mes = values.mes.format('MM-YYYY');
        const gestora = values.gestora || (gestorasDisponiveis.length === 1 ? gestorasDisponiveis[0] : undefined);
        const params = {
            mes,
            gestora,
            ...(beneficioId ? { assistencia: beneficioId } : { favorecido: values.favorecido }),
        };
        setLastParams(params);
        executeBusca(params);
    };

    // Download Excel via fetch + blob (necessário para enviar Basic Auth header)
    const onDownloadExcel = async () => {
        if (!lastParams || loadingExcel) return;

        const queryParams = new URLSearchParams({
            mes: lastParams.mes,
            gestora: lastParams.gestora,
            excel: 'sim',
            ...(lastParams.assistencia ? { assistencia: lastParams.assistencia } : { favorecido: lastParams.favorecido }),
        });

        try {
            setLoadingExcel(true);
            const response = await fetch(
                `${getIntegrarUrl()}/assistencias/trabalhadores-totais?${queryParams}`,
                { method: 'GET', headers: getBasicAuthHeaders() }
            );

            if (!response.ok) {
                message.error('Erro ao gerar o Excel.');
                return;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `trabalhadores-totais-${lastParams.mes}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.success('Excel gerado com sucesso.');
        } catch (_) {
            message.error('Erro ao gerar o Excel.');
        } finally {
            setLoadingExcel(false);
        }
    };

    const columns = [
        { title: 'UF', dataIndex: 'uf', key: 'uf', width: 60 },
        { title: 'Segmento', dataIndex: 'segmento', key: 'segmento', width: 220, ellipsis: true },
        { title: 'Plano', dataIndex: 'plano', key: 'plano', width: 300, ellipsis: true },
        { title: 'Versão', dataIndex: 'versao', key: 'versao', width: 100 },
        {
            title: 'Qtd. Trabalhadores',
            dataIndex: 'qt_trabalhadores',
            key: 'qt_trabalhadores',
            width: 150,
            align: 'right',
            render: (v) => (v ?? 0).toLocaleString('de-DE'),
        },
        {
            title: 'Afastados +12m',
            dataIndex: 'qt_afastados_12m',
            key: 'qt_afastados_12m',
            width: 120,
            align: 'right',
            render: (v) => (v ?? 0).toLocaleString('de-DE'),
        },
        {
            title: 'Idade Máxima',
            dataIndex: 'qt_idade_maxima',
            key: 'qt_idade_maxima',
            width: 110,
            align: 'right',
            render: (v) => (v ?? 0).toLocaleString('de-DE'),
        },
        {
            title: 'Vl. Provisão',
            dataIndex: 'vl_provisao',
            key: 'vl_provisao',
            width: 150,
            align: 'right',
            render: (v) => mask(v ?? 0, 'currency'),
        },
    ];

    const loading = loadingFavorecidos || loadingBusca || loadingGestoras || loadingExcel;

    return (
        <>
            {/* Cabeçalho */}
            <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: '20px 24px' }}>
                <Row align='middle'>
                    <Col>
                        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                            <Icons.TeamOutlined style={{ marginRight: 8 }} />
                            Totalizador de Trabalhadores por Plano
                        </Title>
                        <Text type='secondary' style={{ fontSize: '14px' }}>
                            Visualize a quantidade de trabalhadores e valores de arrecadação agrupados por plano e versão
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* Filtros */}
            <Card
                title={
                    <Space>
                        <Icons.SearchOutlined />
                        <span>Filtros de Pesquisa</span>
                    </Space>
                }
                style={{ marginBottom: 24 }}
                bodyStyle={{ padding: '20px 24px' }}
            >
                <Form
                    form={form}
                    layout='vertical'
                    onFinish={onFinish}
                    onValuesChange={(_, allValues) => {
                        updateFormValid(allValues);
                    }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name='mes'
                                label={
                                    <Space>
                                        <Icons.CalendarOutlined />
                                        <span>Mês de Referência</span>
                                    </Space>
                                }
                                rules={[{ required: true, message: 'Selecione o mês de referência' }]}
                            >
                                <DatePicker
                                    picker='month'
                                    locale={ptBR}
                                    format='MM/YYYY'
                                    placeholder='Selecione o mês'
                                    style={{ width: '100%' }}
                                    size='large'
                                    disabledDate={(current) => current && current >= dayjs().startOf('month')}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name='favorecido'
                                label={
                                    <Space>
                                        <Icons.BankOutlined />
                                        <span>Favorecido</span>
                                    </Space>
                                }
                                rules={[{ required: true, message: 'Selecione o favorecido' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder='Selecione o favorecido'
                                    options={favorecidos}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={handleFavorecidoChange}
                                    size='large'
                                    loading={loadingFavorecidos}
                                />
                            </Form.Item>
                        </Col>

                        {showGestoraField && (
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    name='gestora'
                                    label={
                                        <Space>
                                            <Icons.ClusterOutlined />
                                            <span>Gestora</span>
                                        </Space>
                                    }
                                    rules={[{ required: true, message: 'Selecione a gestora' }]}
                                >
                                    <Select
                                        placeholder='Selecione a gestora'
                                        options={gestorasDisponiveis.map((gestora) => ({
                                            value: gestora,
                                            label: gestora.toUpperCase(),
                                        }))}
                                        size='large'
                                        loading={loadingGestoras}
                                    />
                                </Form.Item>
                            </Col>
                        )}

                        <Col xs={24} sm={12} md={8}>
                            <Form.Item label=' ' colon={false}>
                                <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                                    <Button
                                        type='primary'
                                        htmlType='submit'
                                        size='large'
                                        disabled={!formValid || loading}
                                        loading={loadingBusca}
                                        icon={<Icons.SearchOutlined />}
                                        style={{ width: '100%' }}
                                    >
                                        BUSCAR
                                    </Button>
                                    <Button
                                        size='large'
                                        disabled={!showTable || loading}
                                        loading={loadingExcel}
                                        icon={<Icons.FileExcelOutlined />}
                                        style={{ width: '100%' }}
                                        onClick={onDownloadExcel}
                                    >
                                        EXCEL
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* Painel de totais */}
            {showTable && totaisData && (
                <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
                    <Row gutter={[16, 8]}>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic
                                title='Total Trabalhadores'
                                value={totaisData.qt_trabalhadores ?? 0}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic
                                title='Vl. Provisão'
                                value={mask(totaisData.vl_provisao ?? 0, 'currency')}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic
                                title='Afastados +12m'
                                value={totaisData.qt_afastados_12m ?? 0}
                            />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Statistic
                                title='Idade Máxima'
                                value={totaisData.qt_idade_maxima ?? 0}
                            />
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Tabela */}
            {showTable && (
                <Card>
                    <Spin spinning={loadingBusca}>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            rowKey={(_, index) => `row-${index}`}
                            pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
                            scroll={{ x: 1060 }}
                            locale={{
                                emptyText: (
                                    <div style={{ textAlign: 'center' }}>
                                        <b>Nenhum registro encontrado para os filtros informados.</b>
                                    </div>
                                ),
                            }}
                        />
                    </Spin>
                </Card>
            )}

            {/* Estado vazio */}
            {!showTable && !loadingBusca && (
                <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ color: '#999' }}>
                        <Icons.BarChartOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                        <Title level={4} type='secondary'>
                            Configure os filtros e clique em "BUSCAR"
                        </Title>
                        <Text type='secondary'>
                            Selecione o mês de referência e o favorecido para visualizar o totalizador
                        </Text>
                    </div>
                </Card>
            )}
        </>
    );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(TrabalhadoresTotais);
